import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  IconButton,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Chip,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Popover,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import SendIcon from '@mui/icons-material/Send';
import PauseIcon from '@mui/icons-material/Pause';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentsIcon from '@mui/icons-material/Payments';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate, useLocation } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import CreditNoteItemsTable from '../components/credit-note/CreditNoteItemsTable';
import CreditNotePaymentSection from '../components/credit-note/CreditNotePaymentSection';
import CreditNoteSummary from '../components/credit-note/CreditNoteSummary';
import CreditNoteDetails from '../components/credit-note/CreditNoteDetails';
import CustomerInformation from '../components/credit-note/CustomerInformation';
import NotesSection from '../components/credit-note/NotesSection';
import { useSnackbar } from 'notistack';
import { api } from '../utils/apiService';
import InvoiceItemsTable from '../components/sales-invoice/InvoiceItemsTable';

// Sample data for demonstration
const customers = [
  { id: 0, name: 'Walk In Customer', vatNumber: 'N/A', email: 'N/A' },
  { id: 1, name: 'Abdullah Trading Co.', vatNumber: '310175397400003', email: 'info@abdullahtc.com' },
  { id: 2, name: 'Saudi Technical Industries', vatNumber: '310554324800001', email: 'accounts@stisa.com' },
  { id: 3, name: 'Al-Riyadh Enterprises', vatNumber: '300893276100003', email: 'billing@alriyadh.com' },
];

const products = [
  { id: 1, name: 'Office Desk', description: 'Standard office desk 120x80cm', price: 1200, vat: 15 },
  { id: 2, name: 'Executive Chair', description: 'Ergonomic leather chair', price: 850, vat: 15 },
  { id: 3, name: 'Filing Cabinet', description: '4-drawer metal cabinet', price: 650, vat: 15 },
  { id: 4, name: 'Desktop Computer', description: 'i7 processor, 16GB RAM, 512GB SSD', price: 3500, vat: 15 },
  { id: 5, name: 'Conference Table', description: 'Large oval conference table', price: 4200, vat: 15 },
];

// Sample agents data
const agents = [
  { _id: 'agent1', name: 'Mohammed Al-Harbi', email: 'mohammed@example.com', phone: '966501234567' },
  { _id: 'agent2', name: 'Sara Al-Otaibi', email: 'sara@example.com', phone: '966509876543' },
  { _id: 'agent3', name: 'Khalid Al-Sulaiman', email: 'khalid@example.com', phone: '966505555555' },
];

// Add dummy sales invoices data after the products array
const salesInvoices = [
  {
    id: 'INV-10001',
    number: 'INV-10001',
    date: '2023-06-10',
    customer: customers[1], // Abdullah Trading Co.
    items: [
      { product: products[0], quantity: 2, price: 1200, vatRate: 15, discount: 0 },
      { product: products[1], quantity: 1, price: 850, vatRate: 15, discount: 0 }
    ],
    subtotal: 3250,
    vatAmount: 487.50,
    totalAmount: 3737.50,
    status: 'Paid'
  },
  {
    id: 'INV-10002',
    number: 'INV-10002',
    date: '2023-06-15',
    customer: customers[2], // Saudi Technical Industries
    items: [
      { product: products[2], quantity: 1, price: 650, vatRate: 15, discount: 0 },
      { product: products[3], quantity: 1, price: 3500, vatRate: 15, discount: 0 }
    ],
    subtotal: 4150,
    vatAmount: 622.50,
    totalAmount: 4772.50,
    status: 'Paid'
  },
  {
    id: 'INV-10003',
    number: 'INV-10003',
    date: '2023-06-20',
    customer: customers[3], // Al-Riyadh Enterprises
    items: [
      { product: products[4], quantity: 1, price: 4200, vatRate: 15, discount: 0 }
    ],
    subtotal: 4200,
    vatAmount: 630,
    totalAmount: 4830,
    status: 'Paid'
  }
];

// Add these style constants at the top of the component, after the imports
const controlStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    height: 36,
  },
  '& .MuiInputBase-input': {
    py: 0.75,
    fontSize: '0.875rem'
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem'
  },
  '& .MuiSelect-select': {
    py: 0.75
  }
};

const multilineStyle = {
  ...controlStyle,
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    minHeight: 36,
  }
};

function NewCreditNote() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customerType, setCustomerType] = useState('B2B');
  const [paymentExpanded, setPaymentExpanded] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const location = useLocation();
  
  const [creditNote, setCreditNote] = useState({
    creditNoteNumber: '',
    date: new Date(),
    dueDate: new Date(),
    customer: null,
    agent: null,
    originalInvoice: '',
    reasonForCreditNote: '',
    items: [],
    notes: '',
    terms: 'Payment due within 30 days. Late payments subject to 2% monthly interest.',
    status: 'Draft',
    paymentStatus: 'Paid',
    paymentAmount: '',
    paymentReference: '',
    payments: [],
    additionalCost: '0',
    discountPercentage: '0',
    discountAmount: '0',
    tenantId: '6808c72e7b45a82c7539249b'
  });
  
  // Process the selected invoice data when component mounts
  useEffect(() => {
    if (location.state?.selectedInvoice) {
      const invoiceData = location.state.selectedInvoice;
      console.log('Processing selected invoice from location state:', invoiceData);

      // Check if we have the complete invoice data
      if (invoiceData.data?.master && invoiceData.data?.details) {
        const { master, details } = invoiceData.data;
        
        // Create a formatted invoice object
        const formattedInvoice = {
          invoiceNo: master.invoiceNo,
          invoiceDate: master.invoiceDate,
          number: master.invoiceNo,
          date: master.invoiceDate,
          customerName: master.customerName,
          customerCode: master.customerCode,
          vatNumber: master.vat,
          customerAddress: master.customerAddress,
          customerPhone: master.customerPhone,
          customerType: master.customerType,
          items: details.map(item => ({
            itemId: item.itemId,
            itemCode: item.itemCode,
            description: item.description || '',
            qty: item.qty,
            unitPrice: item.unitPrice,
            discountPerc: item.discountPerc || 0,
            vat: item.vat,
            grandTotal: item.grandTotal
          }))
        };
        
        console.log('Formatted invoice data:', {
          invoiceNo: formattedInvoice.invoiceNo,
          customerName: formattedInvoice.customerName,
          itemsCount: formattedInvoice.items.length,
          totalAmount: formattedInvoice.items.reduce((sum, item) => sum + item.grandTotal, 0)
        });
        
        // Set the selected invoice state
        setSelectedInvoice(formattedInvoice);
        
        // Update the credit note with the formatted invoice data
        setCreditNote(prev => ({
          ...prev,
          originalInvoice: master.invoiceNo,
          customer: {
            customerName: master.customerName,
            customerCode: master.customerCode,
            vat: master.vat || 0,
            customerAddress: master.customerAddress,
            customerPhone: master.customerPhone
          },
          items: details.map(item => ({
          id: item._id || Date.now() + Math.floor(Math.random() * 1000),
          product: {
            id: item.itemId,
            name: item.description || item.itemCode,
            productCode: item.itemCode,
            price: item.unitPrice
          },
          quantity: item.qty,
            originalQuantity: item.qty,
          price: item.unitPrice,
          vatRate: item.vat,
            discount: item.discountPerc || 0,
          description: item.description || '',
            total: item.grandTotal
          }))
        }));
        
        // Set customer type based on the invoice
        setCustomerType(master.customerType || 'B2B');
      } else {
        console.error('Invalid invoice data structure:', invoiceData);
      }
    }
  }, [location.state?.selectedInvoice]);

  // Calculate VAT amount for items
  const calculateVAT = (items) => {
    return items.reduce((total, item) => {
      const lineTotal = item.quantity * item.price;
      const discountAmount = (lineTotal * (item.discount || 0)) / 100;
      const netValue = lineTotal - discountAmount;
      const vatAmount = (netValue * (item.vatRate || 0)) / 100;
      return total + vatAmount;
    }, 0);
  };

  // Calculate net amount for items
  const calculateNetAmount = (items) => {
    return items.reduce((total, item) => {
      const lineTotal = item.quantity * item.price;
      const discountAmount = (lineTotal * (item.discount || 0)) / 100;
      const netValue = lineTotal - discountAmount;
      return total + netValue;
    }, 0);
  };

  // Prepare master data for credit note submission
  const prepareMasterData = (creditNoteData) => {
    return {
      creditNoteNo: creditNoteData.creditNoteNumber,
      refInvoiceNo: creditNoteData.originalInvoice,
      date: new Date().toISOString().split('T')[0],
      invoiceDate: new Date(creditNoteData.date).toISOString().split('T')[0],
      dueDate: new Date(creditNoteData.dueDate).toISOString().split('T')[0],
      customerId: creditNoteData.customer?.customerCode || '',
      customerCode: creditNoteData.customer?.customerCode || '',
      customerName: creditNoteData.customer?.customerName || '',
      customerType: customerType,
      customerPhone: creditNoteData.customer?.customerPhone || '',
      customerAddress: creditNoteData.customer?.customerAddress || '',
      customerStreetName: creditNoteData.customer?.customerStreetName || '',
      customerPostalCode: creditNoteData.customer?.customerPostalCode || '',
      reasonForCreditNote: creditNoteData.reasonForCreditNote,
      totalAmount: calculateTotal(creditNoteData.items),
      discountAmount: parseFloat(creditNoteData.discountAmount || 0),
      discountPercentage: parseFloat(creditNoteData.discountPercentage || 0),
      vatAmount: calculateVAT(creditNoteData.items),
      netAmount: calculateNetAmount(creditNoteData.items),
      additionalCost: parseFloat(creditNoteData.additionalCost || 0),
      notes: creditNoteData.notes || '',
      paymentStatus: (creditNoteData.paymentStatus || 'pending').toLowerCase(),
      items: [],
      grandTotal: calculateTotal(creditNoteData.items),
      originalInvoice: {}
    };
  };

  // Prepare details data for credit note submission
  const prepareDetailsData = (creditNoteData) => {
    return creditNoteData.items.map((item, index) => {
      // Ensure itemId is always set
      if (!item.product || !item.product.id) {
        throw new Error('Product ID is required for all credit note items');
      }
      
      const lineTotal = item.quantity * item.price;
      const discountAmount = (lineTotal * (item.discount || 0)) / 100;
      const netValue = lineTotal - discountAmount;
      const vatAmount = (netValue * (item.vatRate || 0)) / 100;
      const totalWithVat = netValue + vatAmount;
      
      return {
        itemId: item.product?.id || item.product?._id,
        itemName: item.product.name,
        qty: item.quantity,
        unitPrice: item.price,
        uom: (item.unit || item.product?.basicUnit || 'EA').split('(')[0].trim(),
        discountAmount: discountAmount,
        vatPercentage: item.vatRate || 0,
        vatAmount: vatAmount,
        total: totalWithVat,
        grandTotal: totalWithVat,
        netValue: netValue,
        vatAmt: vatAmount,
        totalAmt: totalWithVat,
        priceWithVat: item.price * (1 + (item.vatRate || 0) / 100),
        lineNo: index + 1
      };
    });
  };

  // Validate credit note data before submission
  const validateCreditNote = (creditNoteData) => {
    const errors = [];

    if (!creditNoteData.customer) {
      errors.push('Please select a customer');
    }

    if (!creditNoteData.originalInvoice) {
      errors.push('Please select an original invoice');
    }

    if (!creditNoteData.reasonForCreditNote || creditNoteData.reasonForCreditNote.trim() === '') {
      errors.push('Reason for Credit Note is required');
    }

    if (creditNoteData.items.length === 0) {
      errors.push('Please add at least one item to the credit note');
    }
    
    // Validate that all items have a product selected with a valid ID
    const invalidItems = creditNoteData.items.filter(item => !item.product || !item.product.id);
    if (invalidItems.length > 0) {
      errors.push('All credit note items must have a product selected with a valid ID');
    }

    if (errors.length > 0) {
      throw new Error(errors.join('\n'));
    }
  };

  // Fetch the next credit note number from the API
  const fetchNextCreditNoteNumber = async () => {
    try {
      setLoading(true);
      const response = await api.get('/creditNotes/next-invoice-number');
      
      if (response.data.success) {
        const nextCreditNoteNumber = response.data.data.nextCreditNoteNumber;
        console.log('Received next credit note number:', nextCreditNoteNumber);
        return nextCreditNoteNumber;
      } else {
      console.warn('API did not return success, using fallback credit note number');
      return 'CN-00001'; // Fallback
      }
    } catch (error) {
      console.error('Error fetching next credit note number:', error);
      enqueueSnackbar('Failed to fetch next credit note number', { variant: 'error' });
      return 'CN-00001'; // Fallback
    } finally {
      setLoading(false);
    }
  };

  // Initialize credit note number when component mounts
  useEffect(() => {
    const initializeCreditNoteNumber = async () => {
      const nextNumber = await fetchNextCreditNoteNumber();
      setCreditNote(prev => ({
        ...prev,
        creditNoteNumber: nextNumber
      }));
    };

    initializeCreditNoteNumber();
  }, []); // Empty dependency array means this runs once on mount

  // Reset the credit note form after successful submission
  const resetCreditNoteForm = async () => {
    try {
      setLoading(true);
      const nextNumber = await fetchNextCreditNoteNumber();
    
    setCreditNote({
        creditNoteNumber: nextNumber,
      date: new Date(),
      dueDate: new Date(),
      customer: null,
      agent: null,
      originalInvoice: '',
      reasonForCreditNote: '',
      items: [],
      notes: '',
      terms: 'Payment due within 30 days. Late payments subject to 2% monthly interest.',
      status: 'Draft',
      paymentStatus: 'Paid',
      paymentAmount: '',
      paymentReference: '',
      payments: [],
      additionalCost: '0',
      discountPercentage: '0',
      discountAmount: '0',
        tenantId: creditNote.tenantId
    });
    
    setNewItem({
      product: null,
      description: '',
      quantity: 0,
      price: 0,
      vatRate: 15,
      discount: 0,
    });
    } catch (error) {
      console.error('Error resetting credit note form:', error);
      enqueueSnackbar('Failed to reset credit note form', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Function to submit credit note to backend
  const submitCreditNote = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Validate credit note data first
      try {
      validateCreditNote(creditNote);
      } catch (validationError) {
        const errorMessages = validationError.message.split('\n');
        errorMessages.forEach(message => {
          enqueueSnackbar(message, { 
            variant: 'error',
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center',
            },
            autoHideDuration: 5000
          });
        });
        setLoading(false);
        return;
      }
      
      // Prepare data for submission
      const masterData = prepareMasterData(creditNote);
      const detailData = prepareDetailsData(creditNote);

      const requestBody = {
        masterData,
        detailData
      };
      
      console.log('Credit note request body:', JSON.stringify(requestBody, null, 2));
      
      // Submit to backend
      const response = await api.post('creditNotes', requestBody);

      if (response.data.success) {
        enqueueSnackbar('Credit note created successfully', { 
          variant: 'success',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center',
          }
        });
        await resetCreditNoteForm();
      } else {
        throw new Error(response.data.message || 'Failed to create credit note');
      }
    } catch (error) {
      console.error('Error submitting credit note:', error);
      setError(error.message || 'An error occurred while creating the credit note');
      enqueueSnackbar(
        error.response?.data?.message || error.message || 'Failed to create credit note', 
        { 
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center',
          }
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const [newItem, setNewItem] = useState({
    product: null,
    description: '',
    quantity: 0,
    price: 0,
    vatRate: 15,
    discount: 0,
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({
    description: false,
    discountPerc: false,
    discountAmt: false,
    netValue: false,
    priceWithVat: true,
    vatAmount: true,
    grandTotal: true
  });
  
  const settingsOpen = Boolean(settingsAnchorEl);

  const [termsExpanded, setTermsExpanded] = useState(false);

  const [openAgentDialog, setOpenAgentDialog] = useState(false);
  const [agentDialogMode, setAgentDialogMode] = useState('add'); // 'add' or 'edit'
  const [currentAgent, setCurrentAgent] = useState(null);
  const [newAgent, setNewAgent] = useState({ name: '' });
  const [customAgents, setCustomAgents] = useState([]);

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // State for products data
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  const handleCustomerChange = (event, newValue) => {
    setCreditNote({
      ...creditNote,
      customer: newValue,
    });
  };

  const handleDateChange = (newDate) => {
    setCreditNote({
      ...creditNote,
      date: newDate,
    });
  };

  // Handle invoice selection from CreditNoteDetails component or SelectInvoiceDialog
  const handleInvoiceSelect = async (event, invoice) => {
    if (invoice) {
      try {
        // Get the invoice number from the selected invoice
        const invoiceNo = invoice.invoiceNo || invoice.number;
        console.log('Fetching invoice details for:', invoiceNo);
      
        // Use the correct API URL format with the invoice number
        const response = await api.get(`/sales/${invoiceNo}`);
        console.log('API Response:', response.data);

        if (response.data.success) {
          const invoiceData = response.data.data;
          console.log('Invoice Data:', invoiceData);
        
        // Create a formatted invoice object
        const formattedInvoice = {
            invoiceNo: invoiceData.master.invoiceNo,
            invoiceDate: invoiceData.master.invoiceDate,
            number: invoiceData.master.invoiceNo,
            date: invoiceData.master.invoiceDate,
            customerName: invoiceData.master.customerName,
            customerCode: invoiceData.master.customerCode,
            vatNumber: invoiceData.master.vat,
            customerAddress: invoiceData.master.customerAddress,
            customerPhone: invoiceData.master.customerPhone,
            customerType: invoiceData.master.customerType,
            items: invoiceData.details.map(item => ({
              itemId: item.itemId,
            itemCode: item.itemCode,
            description: item.description || '',
              qty: item.qty,
              unitPrice: item.unitPrice,
              discountPerc: item.discountPerc || 0,
              vat: item.vat,
              grandTotal: item.grandTotal
          }))
        };
        
          console.log('Formatted invoice data:', {
            invoiceNo: formattedInvoice.invoiceNo,
            customerName: formattedInvoice.customerName,
            itemsCount: formattedInvoice.items.length,
            totalAmount: formattedInvoice.items.reduce((sum, item) => sum + item.grandTotal, 0)
          });
          
          // Set the selected invoice state
        setSelectedInvoice(formattedInvoice);
        
          // Update the credit note with the formatted invoice data
        setCreditNote(prev => ({
          ...prev,
            originalInvoice: invoiceData.master.invoiceNo,
          customer: {
              customerName: invoiceData.master.customerName,
              customerCode: invoiceData.master.customerCode,
              vat: invoiceData.master.vat || 0,
              customerAddress: invoiceData.master.customerAddress,
              customerPhone: invoiceData.master.customerPhone
          },
            items: invoiceData.details.map(item => ({
              id: item._id || Date.now() + Math.floor(Math.random() * 1000),
            product: {
                id: item.itemId,
              name: item.description || item.itemCode,
              productCode: item.itemCode,
              price: item.unitPrice
            },
            quantity: item.qty,
            originalQuantity: item.qty,
            price: item.unitPrice,
            vatRate: item.vat,
            discount: item.discountPerc || 0,
            description: item.description || '',
            total: item.grandTotal
          }))
        }));

          // Set customer type based on the invoice
          setCustomerType(invoiceData.master.customerType || 'B2B');
        }
      } catch (error) {
        console.error('Error fetching invoice details:', error);
        // Handle error appropriately
      }
    } else {
      console.log('Clearing invoice selection');
      setSelectedInvoice(null);
      setCreditNote(prev => ({
        ...prev,
        originalInvoice: ''
      }));
    }
  };

  const handleAddItem = () => {
    if (!newItem.product) return;
    
    const newItemId = Date.now(); // Use timestamp for unique ID
    
    setCreditNote({
      ...creditNote,
      items: [
        ...creditNote.items, 
        {
          id: newItemId,
          product: newItem.product,
          description: newItem.description || newItem.product.description,
          quantity: newItem.quantity,
          price: newItem.price || newItem.product.price,
          vatRate: newItem.vatRate || newItem.product.vat,
          discount: newItem.discount,
        }
      ],
    });
    
    setNewItem({
      product: null,
      description: '',
      quantity: 1,
      price: 0,
      vatRate: 15,
      discount: 0,
    });
  };

  const handleRemoveItem = (itemId) => {
    setCreditNote({
      ...creditNote,
      items: creditNote.items.filter(item => item.id !== itemId),
    });
  };

  const calculateItemTotals = (item) => {
    const netValue = item.quantity * item.price;
    const discountAmount = (netValue * item.discount) / 100;
    const netValueAfterDiscount = netValue - discountAmount;
    const vatAmount = (netValueAfterDiscount * item.vatRate) / 100;
    const total = netValueAfterDiscount + vatAmount;

    return {
      netValue,
      discountAmount,
      netValueAfterDiscount,
      vatAmount,
      total
    };
  };

  const handleItemChange = (itemId, field, value) => {
    const updatedItems = creditNote.items.map(item => {
      if (item.id === itemId) {
        // Create a completely new object for the updated item
        const updatedItem = {
          ...item,
          [field]: value,
          // Explicitly preserve originalQuantity
          originalQuantity: item.originalQuantity,
          // Reset any calculated fields to ensure they're recalculated
          netValue: undefined,
          discountAmount: undefined,
          netValueAfterDiscount: undefined,
          vatAmount: undefined,
          total: undefined
        };
        const totals = calculateItemTotals(updatedItem);
        return {
          ...updatedItem,
          ...totals
        };
      }
      // Return a new object for unchanged items to prevent shared references
      return { ...item };
    });

    setCreditNote({
      ...creditNote,
      items: updatedItems
    });
  };

  const handleProductChange = (event, newValue) => {
    setNewItem({
      ...newItem,
      product: newValue,
      price: newValue ? newValue.price : 0,
      description: newValue ? newValue.description : '',
    });
  };

  const calculateSubtotal = (items) => {
    return items.reduce((sum, item) => {
      const totals = calculateItemTotals(item);
      return sum + totals.netValueAfterDiscount;
    }, 0);
  };

  const calculateTotal = (items) => {
    return items.reduce((sum, item) => {
      const totals = calculateItemTotals(item);
      return sum + totals.total;
    }, 0);
  };

  const saveCreditNote = () => {
    // Calculate totals
    const subtotal = calculateSubtotal(creditNote.items);
    const vatAmount = calculateVAT(creditNote.items);
    const totalAmount = calculateTotal(creditNote.items);

    setCreditNote({
      ...creditNote,
      subtotal,
      vatAmount,
      totalAmount,
      status: 'Draft'
    });

    // TODO: Implement actual save logic
    console.log('Saving credit note:', creditNote);
  };

  // const submitCreditNote = () => {
  //   // Calculate totals
  //   const subtotal = calculateSubtotal(creditNote.items);
  //   const vatAmount = calculateVAT(creditNote.items);
  //   const totalAmount = calculateTotal(creditNote.items);

  //   setCreditNote({
  //     ...creditNote,
  //     subtotal,
  //     vatAmount,
  //     totalAmount,
  //     status: 'Submitted'
  //   });

  //   // TODO: Implement actual submission logic
  //   console.log('Submitting credit note:', creditNote);
  // };

  const handleSettingsClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setAnchorEl(null);
  };

  const handleColumnToggle = (column) => {
    setVisibleColumns({
      ...visibleColumns,
      [column]: !visibleColumns[column]
    });
  };

  // Removed duplicate handleInvoiceSelect function

  const handleDueDateChange = (newDate) => {
    setCreditNote({
      ...creditNote,
      dueDate: newDate,
    });
  };

  const handleAgentChange = (event) => {
    const agentId = event.target.value;
    if (!agentId) {
      setCreditNote({
        ...creditNote,
        agent: null
      });
      return;
    }
    
    const selectedAgent = agents.find(agent => agent._id === agentId);
    setCreditNote({
      ...creditNote,
      agent: selectedAgent
    });
  };

  const getTotalPayments = () => {
    return creditNote.payments.reduce((sum, payment) => sum + payment.amount, 0);
  };

  const getRemainingBalance = () => {
    const totalCreditNote = calculateTotal(creditNote.items);
    const totalPaid = getTotalPayments();
    return Math.max(0, totalCreditNote - totalPaid);
  };

  const handleAddPayment = (paymentData) => {
    // New call from PaymentSection component with proper date handling
    let paymentDate;
    
    try {
      if (paymentData.date instanceof Date) {
        paymentDate = paymentData.date;
      } else if (typeof paymentData.date === 'string' && paymentData.date) {
        paymentDate = new Date(paymentData.date);
      } else {
        paymentDate = new Date();
      }
    } catch (e) {
      paymentDate = new Date();
    }
                     
    const newPayment = {
      id: Date.now(),
      date: paymentDate,
      method: paymentData.method || 'Unknown',
      methodId: paymentData.methodId,
      amount: parseFloat(paymentData.amount) || 0,
      reference: paymentDate.reference || '',
      category: paymentData.category || 'unknown'
    };

    setCreditNote({
      ...creditNote,
      payments: [...creditNote.payments, newPayment]
    });
    
    // Reset payment form fields
    setPaymentAmount('');
    setPaymentReference('');
  };

  const handleRemovePayment = (paymentId) => {
    setCreditNote({
      ...creditNote,
      payments: creditNote.payments.filter(payment => payment.id !== paymentId)
    });
  };

  const handleColumnVisibilityChange = (columnName) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnName]: !prev[columnName]
    }));
  };

  const [creditNotes, setCreditNotes] = useState([]);

  const columns = [
    { field: 'invoiceNo', headerName: 'Credit Note #', width: 130 },
    { field: 'invoiceDate', headerName: 'Date', width: 130 },
    { field: 'refInvoiceNo', headerName: 'Ref Invoice No', width: 130 },
    { field: 'customerName', headerName: 'Customer', width: 200 },
    { field: 'subTotal', headerName: 'Total Amount', width: 130, 
      valueFormatter: (params) => params.value?.toFixed(2) },
    { field: 'vatAmt', headerName: 'VAT Amount', width: 130,
      valueFormatter: (params) => params.value?.toFixed(2) },
    { field: 'totalAmt', headerName: 'Grand Total', width: 130,
      valueFormatter: (params) => params.value?.toFixed(2) },
    { field: 'actions', headerName: 'Action', width: 130,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          startIcon={<EditIcon />}
          onClick={() => handleEditCreditNote(params.row)}
        >
          Edit
        </Button>
      )
    }
  ];

  const handleEditCreditNote = (creditNote) => {
    navigate(`/edit-credit-note/${creditNote.invoiceNo}`);
  };

  useEffect(() => {
    // Fetch credit notes from your API
    const fetchCreditNotes = async () => {
      try {
        // Get the selected invoice number from location state
        const selectedInvoiceNo = location.state?.selectedInvoice?.invoiceNo;
        
        if (selectedInvoiceNo) {
          console.log('Fetching sales invoice:', selectedInvoiceNo);
          const response = await api.get(`/sales/${selectedInvoiceNo}`);
          
          if (response.data.success) {
            console.log('Sales invoice data:', response.data);
            setCreditNotes(response.data.data);
          }
        }
      } catch (error) {
        console.error('Error fetching sales invoice:', error);
      }
    };
    
    fetchCreditNotes();
  }, [location.state?.selectedInvoice]); // Add dependency on location state

  // Function to handle credit note changes
  const handleCreditNoteChange = (updater) => {
    if (typeof updater === 'function') {
      setCreditNote(prev => {
        const newState = updater(prev);
        // Only trigger recalculations if relevant fields changed
        if (newState.items !== prev.items || 
            newState.additionalCost !== prev.additionalCost || 
            newState.discountPercentage !== prev.discountPercentage || 
            newState.discountAmount !== prev.discountAmount) {
          return newState;
        }
        // For other fields like reasonForCreditNote, just update without triggering recalculations
        return newState;
      });
    } else {
      setCreditNote(updater);
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      <Paper
        elevation={0}
        sx={{
          p: 0.5,
          borderRadius: '4px',
          maxWidth: '1800px',
          margin: '0 auto',
          boxShadow: 'none',
          border: 'none'
        }}
      >
        {/* Header Section */}
        <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={() => navigate('/credit-notes')}
              sx={{ mr: 0.5, bgcolor: 'rgba(67, 97, 238, 0.1)' }}
            >
              <CloseIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold" sx={{ fontSize: '1.25rem' }}>
                New Credit Note
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '0.875rem' }}>
                Create a new credit note
              </Typography>
            </Box>
          </Box>
          <Box>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/credit-notes')}
              sx={{ mr: 0.5, fontSize: '0.875rem' }}
            >
              Cancel
            </Button>
          </Box>
        </Box>

        {/* Info Alert */}
        <Alert severity="info" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
          This credit note will be submitted to ZATCA upon completion. All fields marked with * are required.
        </Alert>

        {/* Main Form Sections */}
        <Grid container spacing={0.5}>
          {/* Credit Note Details Section */}
          <Grid item xs={12} md={6}>
            <CreditNoteDetails
              creditNote={creditNote}
              salesInvoices={salesInvoices}
              selectedInvoice={selectedInvoice}
              controlStyle={controlStyle}
              onCreditNoteChange={handleCreditNoteChange}
              onDateChange={handleDateChange}
              onInvoiceSelect={handleInvoiceSelect}
            />
          </Grid>

          {/* Customer Information Section */}
          <Grid item xs={12} md={6}>
            <CustomerInformation
              creditNote={creditNote}
              customers={customers}
              controlStyle={controlStyle}
              onCustomerChange={handleCustomerChange}
            />
          </Grid>
        </Grid>

        {/* Items Section */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Credit Note Items
        </Typography>

        {/* Using the new CreditNoteItemsTable component */}
        <CreditNoteItemsTable 
          items={creditNote.items}
          onItemChange={handleItemChange}
          onRemoveItem={handleRemoveItem}
          visibleColumns={visibleColumns}
          products={products}
          onSettingsClick={handleSettingsClick}
          readOnly={location.state?.selectedInvoice ? true : false} // Only allow editing quantity when from an invoice
        />
        
        {/* Add New Item Section */}
        {!location.state?.selectedInvoice && (
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'flex-end' }}>
            <Autocomplete
              options={products}
              getOptionLabel={(option) => option.name || ''}
              value={newItem.product}
              onChange={handleProductChange}
              sx={{ width: 250, mr: 2 }}
              renderInput={(params) => (
                <TextField {...params} label="Select Product" size="small" />
              )}
            />
            <TextField
              label="Quantity"
              value={newItem.quantity}
              onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 0 })}
              type="number"
              size="small"
              InputProps={{ inputProps: { min: 0 } }}
              sx={{ width: 100, mr: 2 }}
            />
            <TextField
              label="Price"
              value={newItem.price}
              onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e.target.value) || 0 })}
              type="number"
              size="small"
              InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              sx={{ width: 100, mr: 2 }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddItem}
              disabled={!newItem.product}
              size="small"
              sx={{ height: 40 }}
            >
              Add Item
            </Button>
          </Box>
        )}
        
        {/* Column Settings Popover */}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleSettingsClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <List>
            {Object.entries(visibleColumns).map(([column, visible]) => (
              <ListItem key={column} button onClick={() => handleColumnToggle(column)}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={visible}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary={column.charAt(0).toUpperCase() + column.slice(1).replace(/([A-Z])/g, ' $1')} />
              </ListItem>
            ))}
          </List>
        </Popover>

        {/* Invoice Summary Section */}
        <Grid container spacing={0.5}>
          <Grid item xs={12} md={6}>
            <Box sx={{ height: '100%' }}>
                <CreditNotePaymentSection 
                  items={creditNote.items}
                  payments={creditNote.payments} 
                  paymentMethod={paymentMethod}
                  paymentAmount={paymentAmount}
                  paymentReference={paymentReference}
                  grandTotal={calculateTotal(creditNote.items)}
                  totalPaid={getTotalPayments()}
                  remainingBalance={getRemainingBalance()}
                  onPaymentMethodChange={setPaymentMethod}
                  onPaymentAmountChange={setPaymentAmount}
                  onPaymentReferenceChange={setPaymentReference}
                  onAddPayment={handleAddPayment} 
                  onRemovePayment={handleRemovePayment}
                  paymentStatus={creditNote.paymentStatus}
                  onPaymentStatusChange={(status) => setCreditNote(prev => ({ ...prev, paymentStatus: status }))}
                  totalBillAmount={selectedInvoice?.items?.reduce((total, item) => total + (item.total || 0), 0) || 0}
                  invoiceId={selectedInvoice?.invoiceNo || selectedInvoice?.number}
                />
            </Box>
          </Grid>
          
          {/* Credit Note Summary */}
          <Grid item xs={12} md={6}>
            <Box sx={{ height: '100%' }}>
              <CreditNoteSummary 
              items={creditNote.items}
              additionalCost={creditNote.additionalCost}
              
              discountPercentage={creditNote.discountPercentage}
              discountAmount={creditNote.discountAmount}
              referenceInvoice={{
                amount: creditNote.originalInvoice?.totalAmount || 0
              }}
              onAdditionalCostChange={(value) => setCreditNote({ ...creditNote, additionalCost: value })}
              onDiscountPercentageChange={(percentage, amount) => setCreditNote({ 
                ...creditNote, 
                discountPercentage: percentage,
                discountAmount: amount
              })}
              onDiscountAmountChange={(amount, percentage) => setCreditNote({ 
                ...creditNote, 
                discountAmount: amount,
                discountPercentage: percentage
              })}
              calculateSubtotal={calculateSubtotal}
              calculateVat={calculateVAT}
              calculateTotal={calculateTotal}
              getTotalPayments={getTotalPayments}
              getRemainingBalance={getRemainingBalance}
            />
            </Box>
          </Grid>
        </Grid>

        {/* Notes Section */}
        <NotesSection
          value={creditNote.notes}
          onChange={(e) => setCreditNote({ ...creditNote, notes: e.target.value })}
          multilineStyle={multilineStyle}
        />

        {/* Action Buttons */}
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            sx={{ fontSize: '0.875rem' }}
            disabled={loading}
          >
            Print
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            onClick={submitCreditNote}
            sx={{ fontSize: '0.875rem' }}
            disabled={loading}
          >
            Submit to ZATCA
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default NewCreditNote; 